import { importJWK, exportJWK, generateKeyPair, jwtVerify, SignJWT } from 'jose';
import { openDB } from 'idb';
import type { IDBPDatabase } from 'idb';

// Database name and version
const DB_NAME = 'raetseljagd-db';
const DB_VERSION = 3; // Increment version to trigger database upgrade

// Interface for the key object
interface KeyData {
  publicKey: CryptoKey | Uint8Array;
  privateKey: CryptoKey | Uint8Array;
}

// Ensure IndexedDB is initialized
let dbPromise: Promise<IDBPDatabase> | null = null;

// Helper function to ensure we have a valid DB connection
export const getDB = async (): Promise<IDBPDatabase> => {
  const db = await initDB();
  if (!db) {
    throw new Error('Failed to initialize IndexedDB');
  }
  return db;
};

// Initialize the IndexedDB
export const initDB = async () => {
  if (!dbPromise) {
    try {
      console.log(`Initializing IndexedDB (${DB_NAME}, version ${DB_VERSION})...`);
      dbPromise = openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, newVersion) {
          console.log(`Upgrading IndexedDB from version ${oldVersion} to ${newVersion}...`);
          
          // Create an object store for keys if it doesn't exist
          if (!db.objectStoreNames.contains('keys')) {
            console.log('Creating keys store');
            db.createObjectStore('keys', { keyPath: 'id' });
          }
          
          // Create an object store for QR scans if it doesn't exist
          if (!db.objectStoreNames.contains('qr_scans')) {
            console.log('Creating qr_scans store');
            db.createObjectStore('qr_scans', { keyPath: 'id', autoIncrement: true });
          }
          
          // Create an object store for user answers if it doesn't exist
          if (!db.objectStoreNames.contains('user_answers')) {
            console.log('Creating user_answers store');
            db.createObjectStore('user_answers', { keyPath: 'id', autoIncrement: true });
          }
          
          // Create an object store for sync queue if it doesn't exist
          if (!db.objectStoreNames.contains('sync_queue')) {
            console.log('Creating sync_queue store');
            db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
          }
          
          // Create an object store for user data if it doesn't exist
          if (!db.objectStoreNames.contains('user_data')) {
            console.log('Creating user_data store');
            db.createObjectStore('user_data', { keyPath: 'id' });
          }
          
          // Group-related stores
          if (!db.objectStoreNames.contains('groups')) {
            console.log('Creating groups store');
            db.createObjectStore('groups', { keyPath: 'id' });
          }
          
          if (!db.objectStoreNames.contains('group_members')) {
            console.log('Creating group_members store');
            const groupMembersStore = db.createObjectStore('group_members', { keyPath: ['group_id', 'user_id'] });
            groupMembersStore.createIndex('by_group_id', 'group_id');
            groupMembersStore.createIndex('by_user_id', 'user_id');
          }
          
          if (!db.objectStoreNames.contains('group_invites')) {
            console.log('Creating group_invites store');
            db.createObjectStore('group_invites', { keyPath: 'code' });
          }
          
          if (!db.objectStoreNames.contains('group_pictures')) {
            console.log('Creating group_pictures store');
            const picturesStore = db.createObjectStore('group_pictures', { keyPath: 'id' });
            picturesStore.createIndex('by_group_id', 'group_id');
          }
          
          if (!db.objectStoreNames.contains('group_progress')) {
            console.log('Creating group_progress store');
            const progressStore = db.createObjectStore('group_progress', { keyPath: 'id' });
            progressStore.createIndex('by_group_id', 'group_id');
            progressStore.createIndex('by_question', ['group_id', 'question_number']);
          }
          
          console.log('Database upgrade complete');
        },
        blocked() {
          console.warn('IndexedDB upgrade blocked. Other tabs might be using the database.');
          alert('⚠️ Datenbank ist gesperrt!\n\nBitte schließe alle anderen Tabs dieser App und lade die Seite neu.\n\n(Database is locked. Please close all other tabs of this app and reload the page.)');
        },
        blocking() {
          console.warn('This tab is blocking a DB upgrade. Please close this tab.');
        },
        terminated() {
          console.error('IndexedDB connection terminated unexpectedly.');
          dbPromise = null; // Reset so we can try again
        }
      });
      
      // Verify database was initialized correctly
      const db = await dbPromise;
      console.log('Database initialized successfully. Object stores:', db.objectStoreNames);
      
      if (!db.objectStoreNames.contains('user_data')) {
        console.error('user_data store was not created during upgrade!');
        // Force a version bump by deleting and recreating
        dbPromise = null;
        const newVersion = DB_VERSION + 1;
        console.log(`Forcing database recreation with version ${newVersion}`);
        await openDB(DB_NAME, newVersion, {
          upgrade(db) {
            console.log('Force creating all stores...');
            // Create all needed object stores
            if (!db.objectStoreNames.contains('keys')) {
              db.createObjectStore('keys', { keyPath: 'id' });
            }
            if (!db.objectStoreNames.contains('qr_scans')) {
              db.createObjectStore('qr_scans', { keyPath: 'id', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains('user_answers')) {
              db.createObjectStore('user_answers', { keyPath: 'id', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains('sync_queue')) {
              db.createObjectStore('sync_queue', { keyPath: 'id', autoIncrement: true });
            }
            if (!db.objectStoreNames.contains('user_data')) {
              db.createObjectStore('user_data', { keyPath: 'id' });
            }
            
            // Group-related stores
            if (!db.objectStoreNames.contains('groups')) {
              db.createObjectStore('groups', { keyPath: 'id' });
            }
            
            if (!db.objectStoreNames.contains('group_members')) {
              const groupMembersStore = db.createObjectStore('group_members', { keyPath: ['group_id', 'user_id'] });
              groupMembersStore.createIndex('by_group_id', 'group_id');
              groupMembersStore.createIndex('by_user_id', 'user_id');
            }
            
            if (!db.objectStoreNames.contains('group_invites')) {
              db.createObjectStore('group_invites', { keyPath: 'code' });
            }
            
            if (!db.objectStoreNames.contains('group_pictures')) {
              const picturesStore = db.createObjectStore('group_pictures', { keyPath: 'id' });
              picturesStore.createIndex('by_group_id', 'group_id');
            }
            
            if (!db.objectStoreNames.contains('group_progress')) {
              const progressStore = db.createObjectStore('group_progress', { keyPath: 'id' });
              progressStore.createIndex('by_group_id', 'group_id');
              progressStore.createIndex('by_question', ['group_id', 'question_number']);
            }
          }
        });
      }
      
    } catch (err) {
      console.error('Error initializing IndexedDB:', err);
      
      // Check if it's a blocked/lock error
      if (err instanceof Error && (
        err.message.includes('blocked') || 
        err.message.includes('lock') ||
        err.name === 'VersionError'
      )) {
        alert('⚠️ Datenbank ist gesperrt!\n\nBitte schließe alle anderen Tabs dieser App und lade die Seite neu.\n\n(Database is locked. Please close all other tabs of this app and reload the page.)');
      }
      
      throw err;
    }
  }
  
  // Return the database instance
  return dbPromise;
};

// Generate EdDSA key pair
export const generateKeys = async () => {
  try {
    // Generate EdDSA key pair
    const keyPair = await generateKeyPair('EdDSA', {
      crv: 'Ed25519',
      extractable: true // Make sure keys are extractable
    });

    // Export keys to JWK format
    const publicKeyJWK = await exportJWK(keyPair.publicKey);
    const privateKeyJWK = await exportJWK(keyPair.privateKey);

    // Store keys in IndexedDB
    const db = await getDB();
    await db.put('keys', {
      id: 'edDSA_keys',
      publicKey: publicKeyJWK,
      privateKey: privateKeyJWK,
      timestamp: new Date().toISOString()
    });

    return { publicKey: keyPair.publicKey, privateKey: keyPair.privateKey };
  } catch (error) {
    console.error('Error generating keys:', error);
    throw error;
  }
};

// Retrieve keys from IndexedDB
export const getKeys = async (): Promise<KeyData | null> => {
  try {
    const db = await getDB();
    const keyData = await db.get('keys', 'edDSA_keys');
    
    if (!keyData) {
      return null;
    }
    
    // Import keys from JWK format
    const publicKey = await importJWK(keyData.publicKey, 'EdDSA');
    const privateKey = await importJWK(keyData.privateKey, 'EdDSA');
    
    return { publicKey, privateKey };
  } catch (error) {
    console.error('Error retrieving keys:', error);
    throw error;
  }
};

// Export keys (for admin backup)
export const exportKeys = async (): Promise<string | null> => {
  try {
    const db = await getDB();
    const keyData = await db.get('keys', 'edDSA_keys');
    
    if (!keyData) {
      return null;
    }
    
    return JSON.stringify({
      publicKey: keyData.publicKey,
      privateKey: keyData.privateKey,
      timestamp: keyData.timestamp
    });
  } catch (error) {
    console.error('Error exporting keys:', error);
    throw error;
  }
};

// Import keys (from admin backup)
export const importKeys = async (keyString: string): Promise<boolean> => {
  try {
    const keyData = JSON.parse(keyString);
    
    // Validate the key data
    if (!keyData.publicKey || !keyData.privateKey) {
      throw new Error('Invalid key data');
    }
    
    // Store keys in IndexedDB
    const db = await getDB();
    await db.put('keys', {
      id: 'edDSA_keys',
      publicKey: keyData.publicKey,
      privateKey: keyData.privateKey,
      timestamp: new Date().toISOString()
    });
    
    return true;
  } catch (error) {
    console.error('Error importing keys:', error);
    throw error;
  }
};

// Create a JWT token for a QR code
export const createQRToken = async (qrNumber: string, emoji?: string): Promise<string | null> => {
  try {
    // Get keys from IndexedDB
    const keys = await getKeys();
    
    if (!keys) {
      console.error('No keys found');
      return null;
    }
    
    // Create payload with QR number and optional emoji
    const payload: { qrNumber: string; emoji?: string } = { qrNumber };
    if (emoji) {
      payload.emoji = emoji;
    }
    
    // Create a JWT token
    const token = await new SignJWT(payload)
      .setProtectedHeader({ alg: 'EdDSA' })
      .setIssuedAt()
      .setExpirationTime('2y') // 2 years expiration
      .sign(keys.privateKey);
    
    return token;
  } catch (error) {
    console.error('Error creating QR token:', error);
    throw error;
  }
};

// Validate a JWT token from a QR code
export const validateQRToken = async (token: string, publicKey: CryptoKey | Uint8Array | JsonWebKey): Promise<{ qrNumber: string; emoji?: string } | null> => {
  try {
    // Verify the JWT token
    const { payload } = await jwtVerify(token, publicKey, {
      algorithms: ['EdDSA']
    });
    
    // Return the QR number and emoji from the token
    return {
      qrNumber: payload.qrNumber as string,
      emoji: payload.emoji as string | undefined
    };
  } catch (error) {
    console.error('Error validating QR token:', error);
    return null;
  }
};